/*
 *   Copyright (c) 2021 Propally.Inc
 *   All rights reserved.
 */
import * as React from "react";
import { useLocation, Redirect, useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";

import { UserDeviceToken, DeviceTypeEnum, RegisteredStatusEnum } from "./api/generated/api";
import { PropallyApiFactory } from "./module/custom_api_factory";
import { PasswordResetPage } from "./pages/renewal_v1/password_reset/PasswordResetPage";
import { SendPasswordRestLinkPage } from "./pages/renewal_v1/password_reset/SendPasswordRestLinkPage";
import { isNativeApp, isIos, isAndroid } from "./utilities/capacitor_logic";
import { getAppDeviceToken, removeAppDeviceToken } from "./utilities/storage";
import { SessionState } from "./view_models/atoms";


const ignoreAuths = [
  { path: "/password_reset/new", Component: SendPasswordRestLinkPage },
  { path: "/password_reset", Component: PasswordResetPage },
];

export const WithAuth: React.FC<React.PropsWithChildren<any>> = (props) => {
  const api = PropallyApiFactory();
  const [session, setSession] = useRecoilState(SessionState);
  const location = useLocation();
  const history = useHistory(); // useHistoryフックを追加
  const path = location.pathname;
  const ignoreAuth = ignoreAuths.find((h) => {
    return h.path === path;
  });

  React.useEffect(() => {
    // To check whether the user is logged-in, here we use an ajax request with session credentials.
    // If the backend returns 200, it means that the user has credentials.
    // Otherwise, we set `logged_in` session state to false to redirect `/login` page.
    (async () => {
      try {
        const user = await api.v1UserGet({
          withCredentials: true,
        });
        setSession({ logged_in: user.status == 200 });

        if (session.logged_in && isNativeApp) {
          // ログイン中 且つ アプリの場合のデバイス関連情報登録処理
          const userDeviceToken: UserDeviceToken = {
            // device_tokenについて
            // 最新のデバイストークン未保存(ローカルストレージにデバイストークンが残っている)場合、デバイストークンとユーザIDを紐付けて登録 or 更新する
            // デバイストークンがない場合(プッシュ通知の許可を許可しないとした場合にはこちらのパターンとなる)、端末情報のみ登録する
            device_token: getAppDeviceToken() ? getAppDeviceToken() : null,
            device_type: isIos ? DeviceTypeEnum.Ios : isAndroid ? DeviceTypeEnum.Android : DeviceTypeEnum.Other,
          };
          api.v1UserDeviceTokenPost(userDeviceToken, { withCredentials: true });
          removeAppDeviceToken();
        }
        if (user.data.data_first_registered_status === RegisteredStatusEnum.Registration) {
          // ユーザ情報が未登録の場合、ユーザ情報登録ページにリダイレクト
          history.push("/first_account");
        }
      } catch (_e) {
        setSession({ logged_in: false });
      }
    })();
  }, []);

  if (
    ignoreAuths
      .map((h) => {
        return h.path;
      })
      .includes(path)
  ) {
    return <ignoreAuth.Component />;
  }
  return session.logged_in ? props.children : <Redirect to="/login" />;
};
